import React, { useEffect, useState, useCallback } from "react";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import Loading from "../components/Loading";
import Modal from "../components/Modal";
import Field from "../components/Field";
import Select from "../components/Select";
import Checkbox from "../components/Checkbox";

moment.locale("fr");

const API = process.env.REACT_APP_API_URL;

const TABS = [
    { key: "companies", label: "Compagnies", icon: "fa-building" },
    { key: "subscriptions", label: "Abonnements", icon: "fa-credit-card" },
    { key: "invoices", label: "Factures", icon: "fa-file-invoice" },
    { key: "users", label: "Utilisateurs", icon: "fa-users" },
    { key: "invitations", label: "Invitations", icon: "fa-envelope" },
];

const COMPANY_STATUS_LABELS = {
    active: "Active",
    suspended: "Suspendue",
    pending_deletion: "Suppression imminente",
};
const COMPANY_STATUS_CLASSES = {
    active: "success",
    suspended: "warning",
    pending_deletion: "danger",
};
const COMPANY_STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "suspended", label: "Suspendue" },
    { value: "pending_deletion", label: "Suppression imminente" },
];

const PLAN_LABELS = { standard: "Standard", route: "Route", logistics: "Logistics" };
const PLAN_OPTIONS = [
    { value: "standard", label: "Standard (225 €)" },
    { value: "route", label: "Route (150 €)" },
    { value: "logistics", label: "Logistics (140 €)" },
];

const SUB_STATUS_LABELS = { inactive: "Inactif", active: "Actif", past_due: "Paiement en attente", suspended: "Suspendu" };
const SUB_STATUS_CLASSES = { inactive: "secondary", active: "success", past_due: "warning", suspended: "danger" };
const SUB_STATUS_OPTIONS = [
    { value: "inactive", label: "Inactif" },
    { value: "active", label: "Actif" },
    { value: "past_due", label: "Paiement en attente" },
    { value: "suspended", label: "Suspendu" },
];

const INV_STATUS_LABELS = { sent: "Envoyée", paid: "Payée", overdue: "En retard", cancelled: "Annulée" };
const INV_STATUS_CLASSES = { sent: "primary", paid: "success", overdue: "danger", cancelled: "secondary" };
const INV_STATUS_OPTIONS = [
    { value: "sent", label: "Envoyée" },
    { value: "paid", label: "Payée" },
    { value: "overdue", label: "En retard" },
    { value: "cancelled", label: "Annulée" },
];

const ROLE_LABELS = { ROLE_SUPER_ADMIN: "Super Admin", ROLE_MANAGER: "Manager", ROLE_EMPLOYEE: "Employé" };
const ROLE_CLASSES = { ROLE_SUPER_ADMIN: "danger", ROLE_MANAGER: "success", ROLE_EMPLOYEE: "secondary" };
const ROLE_OPTIONS = [
    { value: "ROLE_MANAGER", label: "Manager" },
    { value: "ROLE_EMPLOYEE", label: "Employé" },
];

const PERMISSION_OPTIONS = [
    { value: "logistics", label: "Logistique" },
    { value: "routes", label: "Route" },
    { value: "users:manage", label: "Gestion des utilisateurs" },
    { value: "billing:read", label: "Consultation des factures" },
    { value: "credentials:manage", label: "Gestion des identifiants" },
];

function formatDate(iso) {
    if (!iso) return "";
    return moment(iso).format("DD/MM/YYYY");
}

function formatAmount(cents) {
    return (cents / 100).toFixed(2) + " \u20AC";
}

export default function AdminPage() {
    const [activeTab, setActiveTab] = useState("companies");

    return (
        <div>
            <div className="d-flex align-items-center mb-4">
                <h4 className="mb-0">Administration</h4>
            </div>

            <ul className="nav nav-pills mb-4">
                {TABS.map((tab) => (
                    <li className="nav-item" key={tab.key}>
                        <button
                            className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <i className={`fas ${tab.icon} me-2`}></i>
                            {tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            {activeTab === "companies" && <CompaniesTab />}
            {activeTab === "subscriptions" && <SubscriptionsTab />}
            {activeTab === "invoices" && <InvoicesTab />}
            {activeTab === "users" && <UsersTab />}
            {activeTab === "invitations" && <InvitationsTab />}
        </div>
    );
}

// ─── COMPANIES TAB ──────────────────────────────────────────────────────────────

function CompaniesTab() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({ name: "", companyClientId: "" });
    const [createViolations, setCreateViolations] = useState([]);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ id: "", name: "", companyClientId: "", status: "" });
    const [editViolations, setEditViolations] = useState([]);

    const [showHubModal, setShowHubModal] = useState(false);
    const [hubForm, setHubForm] = useState({ companyId: "", companyName: "", id: "", sortingCenterId: "", code: "", address: "" });
    const [hubViolations, setHubViolations] = useState([]);

    const [showDeleteHubModal, setShowDeleteHubModal] = useState(false);
    const [deletingHub, setDeletingHub] = useState(null);

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const res = await authFetch(`${API}/api/companies`);
            if (!res.ok) { setError("Erreur lors du chargement des compagnies"); return; }
            setCompanies(await res.json());
        } catch { setError("Erreur lors du chargement des compagnies"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

    async function handleCreate() {
        try {
            const res = await authFetch(`${API}/api/companies`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: createForm.name,
                    companyClientId: parseInt(createForm.companyClientId, 10),
                }),
            });
            if (!res.ok) {
                const data = await res.json();
                setCreateViolations(data.violations ?? [{ message: data.detail || "Erreur lors de la création" }]);
                return;
            }
            setShowCreateModal(false);
            setCreateForm({ name: "", companyClientId: "" });
            setCreateViolations([]);
            fetchCompanies();
        } catch { setCreateViolations([{ message: "Erreur lors de la création" }]); }
    }

    async function handleEdit() {
        try {
            const res = await authFetch(`${API}/api/companies/${editForm.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: editForm.name,
                    companyClientId: parseInt(editForm.companyClientId, 10),
                    status: editForm.status,
                }),
            });
            if (!res.ok) {
                const data = await res.json();
                setEditViolations(data.violations ?? [{ message: data.detail || "Erreur lors de la modification" }]);
                return;
            }
            setShowEditModal(false);
            setEditViolations([]);
            fetchCompanies();
        } catch { setEditViolations([{ message: "Erreur lors de la modification" }]); }
    }

    async function handleHubSave() {
        try {
            const isEdit = !!hubForm.id;
            const url = isEdit ? `${API}/api/hubs/${hubForm.id}` : `${API}/api/hubs`;
            const method = isEdit ? "PATCH" : "POST";
            const contentType = "application/json";
            const body = {
                sortingCenterId: parseInt(hubForm.sortingCenterId, 10),
                code: hubForm.code,
                address: hubForm.address,
            };
            if (!isEdit) body.company = `/api/companies/${hubForm.companyId}`;

            const res = await authFetch(url, {
                method,
                headers: { "Content-Type": contentType },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const data = await res.json();
                setHubViolations(data.violations ?? [{ message: data.detail || "Erreur lors de la sauvegarde du hub" }]);
                return;
            }
            setShowHubModal(false);
            setHubViolations([]);
            fetchCompanies();
        } catch { setHubViolations([{ message: "Erreur lors de la sauvegarde du hub" }]); }
    }

    async function handleDeleteHub() {
        try {
            const res = await authFetch(`${API}/api/hubs/${deletingHub.id}`, { method: "DELETE" });
            if (res.ok) {
                setShowDeleteHubModal(false);
                setDeletingHub(null);
                fetchCompanies();
            } else { setError("Erreur lors de la suppression du hub"); }
        } catch { setError("Erreur lors de la suppression du hub"); }
    }

    function openEdit(company) {
        setEditForm({
            id: company.id,
            name: company.name,
            companyClientId: String(company.companyClientId ?? ""),
            status: company.status,
        });
        setEditViolations([]);
        setShowEditModal(true);
    }

    function openHub(company) {
        const hub = company.hub;
        setHubForm({
            companyId: company.id,
            companyName: company.name,
            id: hub?.id || "",
            sortingCenterId: String(hub?.sortingCenterId ?? ""),
            code: hub?.code ?? "",
            address: hub?.address ?? "",
        });
        setHubViolations([]);
        setShowHubModal(true);
    }

    return (
        <>
            <Modal
                title="Nouvelle compagnie"
                showModal={showCreateModal}
                setShowModal={setShowCreateModal}
                button={{ close: "Annuler", valid: "Créer" }}
                onSubmit={handleCreate}
                body={
                    <>
                        <Violations violations={createViolations} />
                        <Field name="name" label="Nom" value={createForm.name}
                            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                        <Field name="companyClientId" label="Client ID" type="number" value={createForm.companyClientId}
                            onChange={(e) => setCreateForm({ ...createForm, companyClientId: e.target.value })} />
                    </>
                }
            />
            <Modal
                title="Modifier la compagnie"
                showModal={showEditModal}
                setShowModal={setShowEditModal}
                button={{ close: "Annuler", valid: "Enregistrer" }}
                onSubmit={handleEdit}
                body={
                    <>
                        <Violations violations={editViolations} />
                        <Field name="editName" label="Nom" value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                        <Field name="editClientId" label="Client ID" type="number" value={editForm.companyClientId}
                            onChange={(e) => setEditForm({ ...editForm, companyClientId: e.target.value })} />
                        <Select name="editStatus" label="Statut" value={editForm.status}
                            options={COMPANY_STATUS_OPTIONS}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} />
                    </>
                }
            />
            <Modal
                title={`Hub - ${hubForm.companyName}`}
                showModal={showHubModal}
                setShowModal={setShowHubModal}
                button={{ close: "Annuler", valid: "Enregistrer" }}
                onSubmit={handleHubSave}
                body={
                    <>
                        <Violations violations={hubViolations} />
                        <Field name="sortingCenterId" label="Sorting Center ID" type="number" value={hubForm.sortingCenterId}
                            onChange={(e) => setHubForm({ ...hubForm, sortingCenterId: e.target.value })} />
                        <Field name="hubCode" label="Code" value={hubForm.code}
                            onChange={(e) => setHubForm({ ...hubForm, code: e.target.value })} />
                        <Field name="hubAddress" label="Adresse" value={hubForm.address}
                            onChange={(e) => setHubForm({ ...hubForm, address: e.target.value })} />
                    </>
                }
            />
            <Modal
                title="Supprimer le hub"
                showModal={showDeleteHubModal}
                setShowModal={setShowDeleteHubModal}
                button={{ close: "Annuler", valid: "Confirmer" }}
                onSubmit={handleDeleteHub}
                body={
                    <p>Supprimer le hub <strong>{deletingHub?.code}</strong> ?</p>
                }
            />

            <div className="card">
                <div className="card-header py-3">
                    <div className="row align-items-center">
                        <div className="col-sm-6">
                            <h5 className="mb-0">Compagnies</h5>
                        </div>
                        <div className="col-sm-6 text-sm-end mt-2 mt-sm-0">
                            <button className="btn btn-primary btn-sm" onClick={() => { setCreateForm({ name: "", companyClientId: "" }); setCreateViolations([]); setShowCreateModal(true); }}>
                                <i className="fas fa-plus me-2"></i>Nouvelle compagnie
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? <Loading /> : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : !companies.length ? (
                        <p className="text-muted">Aucune compagnie</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Nom</th>
                                        <th className="text-center">Client ID</th>
                                        <th className="text-center">Statut</th>
                                        <th className="text-center">Hub</th>
                                        <th className="text-center">Créé le</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companies.map((c) => (
                                        <tr key={c.id}>
                                            <td className="fw-bold">{c.name}</td>
                                            <td className="text-center">{c.companyClientId}</td>
                                            <td className="text-center">
                                                <span className={`badge badge-${COMPANY_STATUS_CLASSES[c.status] || "secondary"}`}>
                                                    {COMPANY_STATUS_LABELS[c.status] || c.status}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                {c.hub ? (
                                                    <span className="badge badge-light text-dark">{c.hub.code}</span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className="text-center">{formatDate(c.createdAt)}</td>
                                            <td className="text-center">
                                                <button className="btn btn-link btn-sm text-primary p-1" title="Modifier" onClick={() => openEdit(c)}>
                                                    <i className="fas fa-pen"></i>
                                                </button>
                                                <button className="btn btn-link btn-sm text-secondary p-1" title="Hub" onClick={() => openHub(c)}>
                                                    <i className="fas fa-warehouse"></i>
                                                </button>
                                                {c.hub && (
                                                    <button className="btn btn-link btn-sm text-danger p-1" title="Supprimer le hub" onClick={() => { setDeletingHub(c.hub); setShowDeleteHubModal(true); }}>
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

// ─── SUBSCRIPTIONS TAB ──────────────────────────────────────────────────────────

function SubscriptionsTab() {
    const [subscriptions, setSubscriptions] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({ company: "", plan: "standard", month: "" });
    const [createViolations, setCreateViolations] = useState([]);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ id: "", plan: "", status: "", month: "" });
    const [editViolations, setEditViolations] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [subRes, compRes] = await Promise.all([
                authFetch(`${API}/api/subscriptions`),
                authFetch(`${API}/api/companies`),
            ]);
            if (subRes.ok) setSubscriptions(await subRes.json());
            if (compRes.ok) setCompanies(await compRes.json());
        } catch { setError("Erreur lors du chargement"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    function getCurrentMonth() {
        return moment().format("YYYY-MM");
    }

    async function handleCreate() {
        try {
            const res = await authFetch(`${API}/api/subscriptions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    company: `/api/companies/${createForm.company}`,
                    plan: createForm.plan,
                    month: createForm.month,
                }),
            });
            if (!res.ok) {
                const data = await res.json();
                setCreateViolations(data.violations ?? [{ message: data.detail || "Erreur lors de la création" }]);
                return;
            }
            setShowCreateModal(false);
            setCreateViolations([]);
            fetchData();
        } catch { setCreateViolations([{ message: "Erreur lors de la création" }]); }
    }

    async function handleEdit() {
        try {
            const res = await authFetch(`${API}/api/subscriptions/${editForm.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    plan: editForm.plan,
                    status: editForm.status,
                    month: editForm.month,
                }),
            });
            if (!res.ok) {
                const data = await res.json();
                setEditViolations(data.violations ?? [{ message: data.detail || "Erreur lors de la modification" }]);
                return;
            }
            setShowEditModal(false);
            setEditViolations([]);
            fetchData();
        } catch { setEditViolations([{ message: "Erreur lors de la modification" }]); }
    }

    function openEdit(sub) {
        setEditForm({
            id: sub.id,
            plan: sub.plan,
            status: sub.status,
            month: sub.month || "",
        });
        setEditViolations([]);
        setShowEditModal(true);
    }

    function getCompanyName(sub) {
        if (sub.company?.name) return sub.company.name;
        const found = companies.find((c) => c.id === sub.company?.id || c["@id"] === sub.company);
        return found?.name || "-";
    }

    return (
        <>
            <Modal
                title="Nouvel abonnement"
                showModal={showCreateModal}
                setShowModal={setShowCreateModal}
                button={{ close: "Annuler", valid: "Créer" }}
                onSubmit={handleCreate}
                body={
                    <>
                        <Violations violations={createViolations} />
                        <Select name="company" label="Compagnie" value={createForm.company}
                            options={companies.map((c) => ({ value: c.id, label: c.name }))}
                            onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })} />
                        <Select name="plan" label="Plan" value={createForm.plan}
                            options={PLAN_OPTIONS}
                            onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })} />
                        <Field name="month" label="Mois (AAAA-MM)" value={createForm.month}
                            onChange={(e) => setCreateForm({ ...createForm, month: e.target.value })} />
                    </>
                }
            />
            <Modal
                title="Modifier l'abonnement"
                showModal={showEditModal}
                setShowModal={setShowEditModal}
                button={{ close: "Annuler", valid: "Enregistrer" }}
                onSubmit={handleEdit}
                body={
                    <>
                        <Violations violations={editViolations} />
                        <Select name="editPlan" label="Plan" value={editForm.plan}
                            options={PLAN_OPTIONS}
                            onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })} />
                        <Select name="editStatus" label="Statut" value={editForm.status}
                            options={SUB_STATUS_OPTIONS}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} />
                        <Field name="editMonth" label="Mois (AAAA-MM)" value={editForm.month}
                            onChange={(e) => setEditForm({ ...editForm, month: e.target.value })} />
                    </>
                }
            />

            <div className="card">
                <div className="card-header py-3">
                    <div className="row align-items-center">
                        <div className="col-sm-6">
                            <h5 className="mb-0">Abonnements</h5>
                        </div>
                        <div className="col-sm-6 text-sm-end mt-2 mt-sm-0">
                            <button className="btn btn-primary btn-sm" onClick={() => {
                                setCreateForm({ company: "", plan: "standard", month: getCurrentMonth() });
                                setCreateViolations([]);
                                setShowCreateModal(true);
                            }}>
                                <i className="fas fa-plus me-2"></i>Nouvel abonnement
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? <Loading /> : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : !subscriptions.length ? (
                        <p className="text-muted">Aucun abonnement</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Compagnie</th>
                                        <th className="text-center">Plan</th>
                                        <th className="text-center">Statut</th>
                                        <th className="text-center">Début</th>
                                        <th className="text-center">Fin</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscriptions.map((s) => (
                                        <tr key={s.id}>
                                            <td className="fw-bold">{getCompanyName(s)}</td>
                                            <td className="text-center">
                                                <span className="badge badge-info">{PLAN_LABELS[s.plan] || s.plan}</span>
                                            </td>
                                            <td className="text-center">
                                                <span className={`badge badge-${SUB_STATUS_CLASSES[s.status] || "secondary"}`}>
                                                    {SUB_STATUS_LABELS[s.status] || s.status}
                                                </span>
                                            </td>
                                            <td className="text-center">{formatDate(s.startAt)}</td>
                                            <td className="text-center">{formatDate(s.endAt)}</td>
                                            <td className="text-center">
                                                <button className="btn btn-link btn-sm text-primary p-1" title="Modifier" onClick={() => openEdit(s)}>
                                                    <i className="fas fa-pen"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

// ─── INVOICES TAB ───────────────────────────────────────────────────────────────

function InvoicesTab() {
    const [invoices, setInvoices] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({ company: "", plan: "standard", amountCents: "", issuedAt: "", dueAt: "" });
    const [createViolations, setCreateViolations] = useState([]);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ id: "", status: "" });
    const [editViolations, setEditViolations] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [invRes, compRes] = await Promise.all([
                authFetch(`${API}/api/invoices`),
                authFetch(`${API}/api/companies`),
            ]);
            if (invRes.ok) setInvoices(await invRes.json());
            if (compRes.ok) setCompanies(await compRes.json());
        } catch { setError("Erreur lors du chargement"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    async function handleCreate() {
        try {
            const res = await authFetch(`${API}/api/invoices`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    company: `/api/companies/${createForm.company}`,
                    plan: createForm.plan,
                    amountCents: parseInt(createForm.amountCents, 10),
                    issuedAt: createForm.issuedAt,
                    dueAt: createForm.dueAt,
                }),
            });
            if (!res.ok) {
                const data = await res.json();
                setCreateViolations(data.violations ?? [{ message: data.detail || "Erreur lors de la création" }]);
                return;
            }
            setShowCreateModal(false);
            setCreateViolations([]);
            fetchData();
        } catch { setCreateViolations([{ message: "Erreur lors de la création" }]); }
    }

    async function handleEdit() {
        try {
            const res = await authFetch(`${API}/api/invoices/${editForm.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: editForm.status }),
            });
            if (!res.ok) {
                const data = await res.json();
                setEditViolations(data.violations ?? [{ message: data.detail || "Erreur lors de la modification" }]);
                return;
            }
            setShowEditModal(false);
            setEditViolations([]);
            fetchData();
        } catch { setEditViolations([{ message: "Erreur lors de la modification" }]); }
    }

    function getCompanyName(inv) {
        if (inv.company?.name) return inv.company.name;
        const found = companies.find((c) => c.id === inv.company?.id || c["@id"] === inv.company);
        return found?.name || "-";
    }

    return (
        <>
            <Modal
                title="Nouvelle facture"
                showModal={showCreateModal}
                setShowModal={setShowCreateModal}
                button={{ close: "Annuler", valid: "Créer" }}
                onSubmit={handleCreate}
                body={
                    <>
                        <Violations violations={createViolations} />
                        <Select name="company" label="Compagnie" value={createForm.company}
                            options={companies.map((c) => ({ value: c.id, label: c.name }))}
                            onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })} />
                        <Select name="plan" label="Plan" value={createForm.plan}
                            options={PLAN_OPTIONS}
                            onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })} />
                        <Field name="amountCents" label="Montant (centimes)" type="number" value={createForm.amountCents}
                            onChange={(e) => setCreateForm({ ...createForm, amountCents: e.target.value })} />
                        <Field name="issuedAt" label="Date d'émission (AAAA-MM-JJ)" value={createForm.issuedAt}
                            onChange={(e) => setCreateForm({ ...createForm, issuedAt: e.target.value })} />
                        <Field name="dueAt" label="Date d'échéance (AAAA-MM-JJ)" value={createForm.dueAt}
                            onChange={(e) => setCreateForm({ ...createForm, dueAt: e.target.value })} />
                    </>
                }
            />
            <Modal
                title="Modifier le statut"
                showModal={showEditModal}
                setShowModal={setShowEditModal}
                button={{ close: "Annuler", valid: "Enregistrer" }}
                onSubmit={handleEdit}
                body={
                    <>
                        <Violations violations={editViolations} />
                        <Select name="editInvStatus" label="Statut" value={editForm.status}
                            options={INV_STATUS_OPTIONS}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} />
                    </>
                }
            />

            <div className="card">
                <div className="card-header py-3">
                    <div className="row align-items-center">
                        <div className="col-sm-6">
                            <h5 className="mb-0">Factures</h5>
                        </div>
                        <div className="col-sm-6 text-sm-end mt-2 mt-sm-0">
                            <button className="btn btn-primary btn-sm" onClick={() => {
                                setCreateForm({ company: "", plan: "standard", amountCents: "", issuedAt: moment().format("YYYY-MM-DD"), dueAt: moment().add(10, "days").format("YYYY-MM-DD") });
                                setCreateViolations([]);
                                setShowCreateModal(true);
                            }}>
                                <i className="fas fa-plus me-2"></i>Nouvelle facture
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? <Loading /> : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : !invoices.length ? (
                        <p className="text-muted">Aucune facture</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Compagnie</th>
                                        <th className="text-center">Plan</th>
                                        <th className="text-center">Montant</th>
                                        <th className="text-center">Statut</th>
                                        <th className="text-center">Émission</th>
                                        <th className="text-center">Échéance</th>
                                        <th className="text-center">Paiement</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((inv) => (
                                        <tr key={inv.id}>
                                            <td>{inv.invoiceNumber || "-"}</td>
                                            <td className="fw-bold">{getCompanyName(inv)}</td>
                                            <td className="text-center">
                                                <span className="badge badge-info">{PLAN_LABELS[inv.plan] || inv.plan}</span>
                                            </td>
                                            <td className="text-center">{formatAmount(inv.amountCents)}</td>
                                            <td className="text-center">
                                                <span className={`badge badge-${INV_STATUS_CLASSES[inv.status] || "secondary"}`}>
                                                    {INV_STATUS_LABELS[inv.status] || inv.status}
                                                </span>
                                            </td>
                                            <td className="text-center">{formatDate(inv.issuedAt)}</td>
                                            <td className="text-center">{formatDate(inv.dueAt)}</td>
                                            <td className="text-center">{formatDate(inv.paidAt)}</td>
                                            <td className="text-center">
                                                <button className="btn btn-link btn-sm text-primary p-1" title="Modifier statut" onClick={() => {
                                                    setEditForm({ id: inv.id, status: inv.status });
                                                    setEditViolations([]);
                                                    setShowEditModal(true);
                                                }}>
                                                    <i className="fas fa-pen"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

// ─── USERS TAB ──────────────────────────────────────────────────────────────────

function UsersTab() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingUser, setDeletingUser] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({ firstName: "", lastName: "" });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await authFetch(`${API}/api/users`);
            if (!res.ok) { setError("Erreur lors du chargement des utilisateurs"); return; }
            setUsers(await res.json());
        } catch { setError("Erreur lors du chargement des utilisateurs"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    async function handleDelete() {
        try {
            const res = await authFetch(`${API}/api/users/${deletingUser.id}`, { method: "DELETE" });
            if (res.ok) {
                setShowDeleteModal(false);
                setDeletingUser(null);
                fetchUsers();
            } else { setError("Erreur lors de la suppression"); }
        } catch { setError("Erreur lors de la suppression"); }
    }

    async function handleRestore(user) {
        try {
            const res = await authFetch(`${API}/api/users/${user.id}/restore`, { method: "POST" });
            if (res.ok) { fetchUsers(); }
            else { setError("Erreur lors de la restauration"); }
        } catch { setError("Erreur lors de la restauration"); }
    }

    async function handleEdit() {
        try {
            const res = await authFetch(`${API}/api/users/${editingUser.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ firstName: editForm.firstName, lastName: editForm.lastName }),
            });
            if (res.ok) {
                setShowEditModal(false);
                setEditingUser(null);
                fetchUsers();
            } else { setError("Erreur lors de la modification"); }
        } catch { setError("Erreur lors de la modification"); }
    }

    const filtered = users.filter((u) =>
        u.firstName?.toLowerCase().includes(search.toLowerCase()) ||
        u.lastName?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <>
            <Modal
                title="Supprimer l'utilisateur"
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                button={{ close: "Annuler", valid: "Confirmer" }}
                onSubmit={handleDelete}
                body={
                    <p>Supprimer l'utilisateur <strong>{deletingUser?.firstName} {deletingUser?.lastName}</strong> ({deletingUser?.email}) ?</p>
                }
            />
            <Modal
                title="Modifier l'utilisateur"
                showModal={showEditModal}
                setShowModal={setShowEditModal}
                button={{ close: "Annuler", valid: "Enregistrer" }}
                onSubmit={handleEdit}
                body={
                    <>
                        <Field name="editFirstName" label="Prénom" value={editForm.firstName}
                            onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
                        <Field name="editLastName" label="Nom" value={editForm.lastName}
                            onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
                    </>
                }
            />
            <div className="card">
                <div className="card-header py-3">
                    <h5 className="mb-0">Utilisateurs</h5>
                </div>
                <div className="card-body">
                    {loading ? <Loading /> : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : (
                        <>
                            <div className="mb-4" style={{ maxWidth: 400 }}>
                                <Field name="searchUsers" label="Rechercher" value={search}
                                    onChange={(e) => setSearch(e.target.value)} margin={false} />
                            </div>
                            {!filtered.length ? (
                                <p className="text-muted">Aucun utilisateur</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Utilisateur</th>
                                                <th className="text-center">Email</th>
                                                <th className="text-center">Rôle</th>
                                                <th className="text-center">Compagnie</th>
                                                <th className="text-center">Statut</th>
                                                <th className="text-center">Créé le</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map((u) => (
                                                <tr key={u.id}>
                                                    <td className="fw-bold">{u.firstName} {u.lastName}</td>
                                                    <td className="text-center">{u.email}</td>
                                                    <td className="text-center">
                                                        <span className={`badge badge-${ROLE_CLASSES[u.role] || "secondary"}`}>
                                                            {ROLE_LABELS[u.role] || u.role}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">{u.company?.name || "-"}</td>
                                                    <td className="text-center">
                                                        {u.deletedAt ? (
                                                            <i className="fa-solid fa-circle-xmark text-danger"></i>
                                                        ) : (
                                                            <i className="fa-solid fa-circle-check text-success"></i>
                                                        )}
                                                    </td>
                                                    <td className="text-center">{formatDate(u.createdAt)}</td>
                                                    <td className="text-center">
                                                        {u.role !== "ROLE_SUPER_ADMIN" && (
                                                            <>
                                                                {u.deletedAt ? (
                                                                    <button className="btn btn-link btn-sm text-success p-1" title="Restaurer"
                                                                        onClick={() => handleRestore(u)}>
                                                                        <i className="fas fa-rotate-left"></i>
                                                                    </button>
                                                                ) : (
                                                                    <>
                                                                        <button className="btn btn-link btn-sm text-primary p-1" title="Modifier"
                                                                            onClick={() => { setEditingUser(u); setEditForm({ firstName: u.firstName, lastName: u.lastName }); setShowEditModal(true); }}>
                                                                            <i className="fas fa-pen"></i>
                                                                        </button>
                                                                        <button className="btn btn-link btn-sm text-danger p-1" title="Supprimer"
                                                                            onClick={() => { setDeletingUser(u); setShowDeleteModal(true); }}>
                                                                            <i className="fas fa-trash"></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

// ─── INVITATIONS TAB ────────────────────────────────────────────────────────────

function InvitationsTab() {
    const [invitations, setInvitations] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({ email: "", firstName: "", lastName: "", company: "", role: "ROLE_EMPLOYEE", permissions: [] });
    const [createViolations, setCreateViolations] = useState([]);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingInvitation, setDeletingInvitation] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [invRes, compRes] = await Promise.all([
                authFetch(`${API}/api/invitations`),
                authFetch(`${API}/api/companies`),
            ]);
            if (invRes.ok) setInvitations(await invRes.json());
            if (compRes.ok) setCompanies(await compRes.json());
        } catch { setError("Erreur lors du chargement"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    async function handleCreate() {
        try {
            const res = await authFetch(`${API}/api/invitations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: createForm.email,
                    firstName: createForm.firstName,
                    lastName: createForm.lastName,
                    company: `/api/companies/${createForm.company}`,
                    role: createForm.role,
                    permissions: createForm.permissions,
                }),
            });
            if (!res.ok) {
                const data = await res.json();
                setCreateViolations(data.violations ?? [{ message: data.detail || "Erreur lors de l'envoi" }]);
                return;
            }
            setShowCreateModal(false);
            setCreateViolations([]);
            fetchData();
        } catch { setCreateViolations([{ message: "Erreur lors de l'envoi" }]); }
    }

    async function handleDelete() {
        try {
            const res = await authFetch(`${API}/api/invitations/${deletingInvitation.id}`, { method: "DELETE" });
            if (res.ok) {
                setShowDeleteModal(false);
                setDeletingInvitation(null);
                fetchData();
            } else { setError("Erreur lors de la suppression"); }
        } catch { setError("Erreur lors de la suppression"); }
    }

    function getCompanyName(inv) {
        if (inv.company?.name) return inv.company.name;
        const found = companies.find((c) => c.id === inv.company?.id || c["@id"] === inv.company);
        return found?.name || "-";
    }

    return (
        <>
            <Modal
                title="Supprimer l'invitation"
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                button={{ close: "Annuler", valid: "Confirmer" }}
                onSubmit={handleDelete}
                body={
                    <p>
                        Supprimer l'invitation de <strong>{deletingInvitation?.firstName} {deletingInvitation?.lastName}</strong> ({deletingInvitation?.email}) ?
                    </p>
                }
            />
            <Modal
                title="Nouvelle invitation"
                showModal={showCreateModal}
                setShowModal={setShowCreateModal}
                button={{ close: "Annuler", valid: "Inviter" }}
                onSubmit={handleCreate}
                body={
                    <>
                        <Violations violations={createViolations} />
                        <Select name="invCompany" label="Compagnie" value={createForm.company}
                            options={companies.map((c) => ({ value: c.id, label: c.name }))}
                            onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })} />
                        <Field name="invFirstName" label="Prénom" value={createForm.firstName}
                            onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} />
                        <Field name="invLastName" label="Nom" value={createForm.lastName}
                            onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} />
                        <Field name="invEmail" label="Email" type="email" value={createForm.email}
                            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
                        <Select name="invRole" label="Rôle" value={createForm.role}
                            options={ROLE_OPTIONS}
                            onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })} />
                        <hr />
                        <h6>Permissions</h6>
                        {PERMISSION_OPTIONS.map((opt) => (
                            <Checkbox
                                key={opt.value}
                                name={opt.value}
                                value={opt.value}
                                label={opt.label}
                                checked={createForm.permissions.includes(opt.value)}
                                onChange={(e) => {
                                    const perms = e.target.checked
                                        ? [...createForm.permissions, opt.value]
                                        : createForm.permissions.filter((p) => p !== opt.value);
                                    setCreateForm({ ...createForm, permissions: perms });
                                }}
                            />
                        ))}
                    </>
                }
            />

            <div className="card">
                <div className="card-header py-3">
                    <div className="row align-items-center">
                        <div className="col-sm-6">
                            <h5 className="mb-0">Invitations</h5>
                        </div>
                        <div className="col-sm-6 text-sm-end mt-2 mt-sm-0">
                            <button className="btn btn-primary btn-sm" onClick={() => {
                                setCreateForm({ email: "", firstName: "", lastName: "", company: "", role: "ROLE_EMPLOYEE", permissions: [] });
                                setCreateViolations([]);
                                setShowCreateModal(true);
                            }}>
                                <i className="fas fa-plus me-2"></i>Nouvelle invitation
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? <Loading /> : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : !invitations.length ? (
                        <p className="text-muted">Aucune invitation en attente</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Nom</th>
                                        <th className="text-center">Email</th>
                                        <th className="text-center">Compagnie</th>
                                        <th className="text-center">Rôle</th>
                                        <th className="text-center">Envoyée le</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invitations.map((inv) => (
                                        <tr key={inv.id}>
                                            <td className="fw-bold">{inv.firstName} {inv.lastName}</td>
                                            <td className="text-center">{inv.email}</td>
                                            <td className="text-center">{getCompanyName(inv)}</td>
                                            <td className="text-center">
                                                <span className={`badge badge-${ROLE_CLASSES[inv.role] || "secondary"}`}>
                                                    {ROLE_LABELS[inv.role] || inv.role}
                                                </span>
                                            </td>
                                            <td className="text-center">{formatDate(inv.createdAt)}</td>
                                            <td className="text-center">
                                                <button className="btn btn-link btn-sm text-danger p-1" title="Supprimer"
                                                    onClick={() => { setDeletingInvitation(inv); setShowDeleteModal(true); }}>
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

// ─── SHARED COMPONENTS ──────────────────────────────────────────────────────────

function Violations({ violations }) {
    if (!violations?.length) return null;
    return (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="fas fa-times-circle me-3"></i>
            <ul className="mb-0 list-unstyled">
                {violations.map((v, i) => (
                    <li key={i}>{v.message || v.propertyPath + ": " + v.message}</li>
                ))}
            </ul>
        </div>
    );
}
